import type { SimulationSummary } from "../engine/simulationSummary.ts";
import { unitSystemOf } from "../model/units.ts";
import { buildSimulationSummaryCsv } from "../model/results/simulationSummaryExport.ts";
import { downloadTextFile } from "../model/downloadFile.ts";
import { useLanguage, useT } from "../i18n/hooks.ts";
import { formatNumber } from "../i18n/formatNumber.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";

/** Project-wide simulation summary — a real runoff continuity check; deliberately no fabricated flow-routing continuity (see simulationSummary.ts). */
export function SimulationSummaryDialog({
  summary,
  flowUnits,
  onClose,
}: {
  summary: SimulationSummary;
  flowUnits: string;
  onClose: () => void;
}) {
  const t = useT();
  const language = useLanguage();
  const sys = unitSystemOf(flowUnits);
  const lengthUnit = sys === "US" ? "ft" : "m";
  const rainUnit = sys === "US" ? "in" : "mm";
  const flowUnitLabel = flowUnits.toLowerCase();
  const continuity = summary.runoffContinuity;

  return (
    <Dialog title={t("simulationSummary.title")} onClose={onClose} wide>
      <div className="simulation-summary-body">
        <section className="simulation-summary-section">
          <h4>{t("simulationSummary.runoffContinuity")}</h4>
          {continuity ? (
            <dl className="report-stat-list">
              {continuity.hasInitLidStorage && (
                <div className="report-stat-row">
                  <dt>{t("simulationSummary.initLidStorage")}</dt>
                  <dd>
                    {formatNumber(continuity.initLidStorageDepth, language, 3)} {rainUnit}
                  </dd>
                </div>
              )}
              <div className="report-stat-row">
                <dt>{t("simulationSummary.totalPrecip")}</dt>
                <dd>
                  {formatNumber(continuity.totalPrecipDepth, language, 3)} {rainUnit}
                </dd>
              </div>
              {continuity.hasOutfallRunon && (
                <div className="report-stat-row">
                  <dt>{t("simulationSummary.outfallRunon")}</dt>
                  <dd>
                    {formatNumber(continuity.totalRunonDepth, language, 3)} {rainUnit}
                  </dd>
                </div>
              )}
              <div className="report-stat-row">
                <dt>{t("simulationSummary.evapLoss")}</dt>
                <dd>
                  {formatNumber(continuity.totalEvapDepth, language, 3)} {rainUnit}
                </dd>
              </div>
              <div className="report-stat-row">
                <dt>{t("simulationSummary.infilLoss")}</dt>
                <dd>
                  {formatNumber(continuity.totalInfilDepth, language, 3)} {rainUnit}
                </dd>
              </div>
              <div className="report-stat-row">
                <dt>{t("simulationSummary.surfaceRunoff")}</dt>
                <dd>
                  {formatNumber(continuity.totalRunoffDepth, language, 3)} {rainUnit}
                </dd>
              </div>
              {continuity.hasLidDrainage && (
                <div className="report-stat-row">
                  <dt>{t("simulationSummary.lidDrainage")}</dt>
                  <dd>
                    {formatNumber(continuity.lidDrainageDepth, language, 3)} {rainUnit}
                  </dd>
                </div>
              )}
              <div className="report-stat-row">
                <dt>{t("simulationSummary.finalStorage")}</dt>
                <dd>
                  {formatNumber(continuity.finalStorageDepth, language, 3)} {rainUnit}
                </dd>
              </div>
              <div className="report-stat-row report-stat-row-highlight">
                <dt>{t("simulationSummary.continuityError")}</dt>
                <dd>
                  {continuity.continuityErrorPct !== null
                    ? formatNumber(continuity.continuityErrorPct, language, 3)
                    : "-"}{" "}
                  %
                </dd>
              </div>
            </dl>
          ) : (
            <p className="property-panel-hint">{t("simulationSummary.noRunoff")}</p>
          )}
        </section>

        <section className="simulation-summary-section">
          <h4>{t("simulationSummary.extremes")}</h4>
          <div className="simulation-summary-extremes">
            <div>
              <h5>{t("simulationSummary.topNodeDepths")}</h5>
              {summary.topNodeDepths.length > 0 ? (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>{t("simulationSummary.node")}</th>
                      <th className="report-table-num">{t("results.depth")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.topNodeDepths.map((e) => (
                      <tr key={e.name}>
                        <td>{e.name}</td>
                        <td className="report-table-num">
                          {formatNumber(e.value, language, 3)} {lengthUnit}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="property-panel-hint">{t("simulationSummary.noResults")}</p>
              )}
            </div>
            <div>
              <h5>{t("simulationSummary.topLinkFlows")}</h5>
              {summary.topLinkFlows.length > 0 ? (
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>{t("simulationSummary.link")}</th>
                      <th className="report-table-num">{t("results.flow")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.topLinkFlows.map((e) => (
                      <tr key={e.name}>
                        <td>{e.name}</td>
                        <td className="report-table-num">
                          {formatNumber(e.value, language, 3)} {flowUnitLabel}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="property-panel-hint">{t("simulationSummary.noResults")}</p>
              )}
            </div>
          </div>
          {summary.peakSystemOutflow !== null && (
            <p className="simulation-summary-outflow">
              {t("simulationSummary.peakSystemOutflow")}:{" "}
              {formatNumber(summary.peakSystemOutflow, language, 3)} {flowUnitLabel}
            </p>
          )}
        </section>
      </div>
      <DialogActions
        primary={{ label: t("common.ok"), onClick: onClose }}
        secondary={{
          label: t("simulationSummary.exportCsv"),
          onClick: () =>
            downloadTextFile(
              buildSimulationSummaryCsv(summary, flowUnits),
              "simulation-summary.csv"
            ),
        }}
      />
    </Dialog>
  );
}
